/**
 * Mobile remote bootstrap, controls-only, no portal auth.
 */
import { WsClient, type WsConnectionStatus } from "./ws-client";
import { EMDR_KINDEREN_ENABLED } from "../lib/feature-flags";
import { isViewerReport, type ClientPresence } from "../lib/beeldbellen-status";
import { newSessionSeed } from "../lib/session-random";

export type RemoteCallbacks = {
  onStatus: (status: WsConnectionStatus, attempt: number) => void;
  onPlayingChange: (playing: boolean) => void;
  onSudChange: (open: boolean) => void;
  onAudienceChange: (audience: "volwassenen" | "kinderen") => void;
  onHostGone: () => void;
  onHostOffline: () => void;
  onHostOnline: () => void;
  onClosed: () => void;
  /** Beeldbellen: wat de cliëntweergave meldt, en de link die de cliënt opent. */
  onClientChange?: (presence: ClientPresence, clientLink: string | null) => void;
};

let client: WsClient | null = null;
let applyingRemote = false;
let fullscreenListeners = new Set<(fullscreen: boolean) => void>();

function notifyFullscreen(fullscreen: boolean): void {
  for (const listener of fullscreenListeners) listener(fullscreen);
}

export function connectRemote(code: string, callbacks: RemoteCallbacks): WsClient {
  client?.disconnect();
  client = new WsClient();

  client.onStatus(callbacks.onStatus);

  const presence: ClientPresence = { count: 0, everJoined: false, report: null };
  let clientLink: string | null = null;
  const notifyClient = () => callbacks.onClientChange?.({ ...presence }, clientLink);
  const onViewerPresence = (msg: { count?: number }) => {
    presence.count = typeof msg.count === "number" ? msg.count : 0;
    if (presence.count > 0) presence.everJoined = true;
    else presence.report = null;
    notifyClient();
  };
  client.on("viewer-joined", onViewerPresence);
  client.on("viewer-left", onViewerPresence);
  client.on("viewer-status", (msg) => {
    if (!isViewerReport(msg.data)) return;
    presence.report = msg.data;
    notifyClient();
  });

  client.on("sync", (msg) => {
    applyingRemote = true;
    if (typeof msg.isPaid === "boolean") {
      window.__EMDR_IS_PAID__ = msg.isPaid;
      window.__EMDR_REFRESH_PAID_UI__?.();
    }
    if (msg.data && typeof msg.data === "object") {
      window.__EMDR_APPLY_REMOTE_STATE__?.(msg.data);
    }
    applyingRemote = false;
    callbacks.onAudienceChange(window.__EMDR_GET_AUDIENCE__?.() ?? "volwassenen");
    if (typeof msg.playing === "boolean") {
      callbacks.onPlayingChange(msg.playing);
    }
    if (typeof msg.sud === "boolean") {
      callbacks.onSudChange(msg.sud);
    }
    if (typeof msg.fullscreen === "boolean") {
      notifyFullscreen(msg.fullscreen);
    }
    if (typeof msg.viewerLink === "string") {
      clientLink = msg.viewerLink;
      notifyClient();
    }
  });

  client.on("play", (msg) => {
    if (typeof msg.playing === "boolean") {
      callbacks.onPlayingChange(msg.playing);
    }
  });

  client.on("fullscreen", (msg) => {
    if (typeof msg.fullscreen === "boolean") {
      notifyFullscreen(msg.fullscreen);
    }
  });

  client.on("sud", (msg) => {
    if (typeof msg.open === "boolean") {
      callbacks.onSudChange(msg.open);
    }
  });

  client.on("host-gone", () => {
    callbacks.onHostGone();
  });

  client.on("host-offline", () => {
    callbacks.onHostOffline();
  });

  client.on("host-online", () => {
    callbacks.onHostOnline();
  });

  window.__EMDR_ON_STATE_CHANGE__ = () => {
    if (applyingRemote) return;
    callbacks.onAudienceChange(window.__EMDR_GET_AUDIENCE__?.() ?? "volwassenen");
    const data = window.__EMDR_GET_STATE__?.() ?? {};
    client?.send({ type: "state", data });
  };

  client.connect(code, "remote");
  return client;
}

export function sendPlay(playing: boolean): void {
  // De seed gaat mee, zodat het grote scherm en de cliëntweergave met hetzelfde toeval starten.
  client?.send(playing ? { type: "play", playing, seed: newSessionSeed() } : { type: "play", playing });
}

export function sendSud(open: boolean): void {
  client?.send(
    open
      ? { type: "sud", open, resume: false }
      : { type: "sud", open, resume: true, seed: newSessionSeed() }
  );
}

export function sendFullscreen(fullscreen: boolean): void {
  client?.send({ type: "fullscreen", fullscreen });
  notifyFullscreen(fullscreen);
}

export function subscribeRemoteFullscreen(
  listener: (fullscreen: boolean) => void
): () => void {
  fullscreenListeners.add(listener);
  return () => fullscreenListeners.delete(listener);
}

/** Beëindigt de sessie: alles pauzeert en de cliënt ziet dat het klaar is. */
export function sendRemoteExit(): void {
  client?.send({ type: "session-end" });
  client?.send({ type: "remote-exit" });
  client?.disconnect();
  client = null;
  window.__EMDR_ON_STATE_CHANGE__ = undefined;
  fullscreenListeners.clear();
}

export function disconnectRemote(): void {
  client?.disconnect();
  client = null;
  window.__EMDR_ON_STATE_CHANGE__ = undefined;
  fullscreenListeners.clear();
}

export function getRemoteClient(): WsClient | null {
  return client;
}

export function bootstrapRemoteEmdr(options: {
  isPaid: boolean;
  onPaywall?: (feature: string) => void;
}): Promise<void> {
  window.__EMDR_REMOTE_MODE__ = true;
  window.__EMDR_IS_PAID__ = options.isPaid;
  window.__EMDR_ON_PAYWALL__ = options.onPaywall || (() => {});

  return import("./figures").then(() =>
    import("./workload").then(() =>
      (EMDR_KINDEREN_ENABLED ? import("./workload-kinderen") : Promise.resolve()).then(() =>
        (EMDR_KINDEREN_ENABLED ? import("./kinderen-werkgeheugen-mode") : Promise.resolve()).then(() =>
          import("./racebaan").then(() =>
            import("./danspas").then(() =>
              import("./experimental").then(() =>
                import("./emdr-core").then(({ runEmdrInit }) => {
                  runEmdrInit();
                })
              )
            )
          )
        )
      )
    )
  );
}
